// src/pages/firm/role/FirmRoleDialog.tsx
import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Save, X, Shield, Zap, Info, AlertCircle } from "lucide-react"; 
import { IconTextButton } from "../../../components/ButtonComponents";
import { RoleType, PermissionGroupDef } from "../../../types/role";
import { DynamicPermissionTree } from "../../../components/DynamicPermissionTree";  
import { useRoleManagementStore } from "../../../store/roleManagementStore";

interface FirmRoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  role: RoleType | null;
  onSave: (data: any) => Promise<void>;
}

export const FirmRoleDialog: React.FC<FirmRoleDialogProps> = ({ isOpen, onClose, role, onSave }) => {
  const { fetchActiveModules, fetchPermissionsSchema } = useRoleManagementStore();
  const [formData, setFormData] = useState({ name: "" });
  const [activeModules, setActiveModules] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [permissionsSchema, setPermissionsSchema] = useState<PermissionGroupDef[]>([]);

  // GÜVENLİK KONTROLÜ: firmId null ise bu bir sistem rolüdür ve düzenlenemez.
  const isReadOnly = useMemo(() => {
    return role ? role.firmId === null : false;
  }, [role]);

  useEffect(() => {
    if (!isOpen) return;

    // Aktif modülleri getir
    fetchActiveModules().then(setActiveModules);
    fetchPermissionsSchema().then(setPermissionsSchema);

    if (role) {
      setFormData({ 
        name: role.name || "", 
        
      });
      setPermissions(role.permissions || []);
    } else {
      setFormData({ name: ""  });
      setPermissions([]);
    }
  }, [isOpen, role]);

  const handleSubmit = async () => {
    if (isReadOnly) return; // Güvenlik önlemi
    const payload = { ...formData, permissions };
    await onSave(payload);
  };

  return (
    <Dialog 
      header={
        <div className="flex items-center gap-2">
          {isReadOnly ? <Shield className="text-amber-500" size={20}/> : <Shield className="text-indigo-600" size={20}/>}
          <span className="font-bold">{role ? (isReadOnly ? "Sistem Rolünü Görüntüle" : "Rol Düzenle") : "Yeni Firma Rolü"}</span>
        </div>
      } 
      visible={isOpen} 
      onHide={onClose} 
      modal 
      maximizable
      className="w-full lg:w-10/12 xl:w-8/12"
      footer={
        <div className="flex justify-end gap-2 border-t p-4">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">
            {isReadOnly ? "Kapat" : "Vazgeç"}
          </IconTextButton>
          {!isReadOnly && (
            <IconTextButton onClick={handleSubmit} icon={Save} className="btn-new">
              Kaydet
            </IconTextButton>
          )}
        </div>
      }
      contentClassName="p-0"
    >
      <div className="flex flex-col lg:flex-row w-full h-full min-h-[60vh] bg-bgAlt text-text">
        
        {/* Left Column - Basic Info */}
        <div className="w-full lg:w-1/3 p-6 border-b lg:border-b-0 lg:border-r border-borderColor space-y-6 bg-card">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2 mb-4 text-text">
              <Zap className="w-5 h-5 text-yellow-500" />
              Temel Bilgiler
            </h3>
            <p className="text-xs text-secondary mb-6">Rolün temel özelliklerini belirleyin.</p>
          </div>

          <div className="space-y-4">
            {isReadOnly && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3 text-amber-800 text-xs mb-4">
                <AlertCircle size={20} className="shrink-0"/>
                <div>
                  <p className="font-bold text-sm mb-1">Sistem Rolü</p>
                  <p className="opacity-90 leading-relaxed">Bu varsayılan bir sistem rolüdür. Güvenlik nedeniyle ismi ve yetkileri değiştirilemez.</p>
                </div>
              </div>
            )}
            
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Rol Adı</label>
              <InputText 
                disabled={isReadOnly}
                value={formData.name} 
                onChange={(e) => setFormData(p=>({...p, name:e.target.value}))} 
                placeholder="Örn: Saha Satış Sorumlusu"
                className={`w-full ${isReadOnly ? "font-semibold opacity-80" : ""}`}
              />
            </div>

            {!isReadOnly && (
              <div className="mt-8 flex items-start gap-3 p-4 bg-primary/10 border border-primary/20 rounded-xl text-xs">
                <div className="p-1.5 bg-primary/20 rounded-lg text-primary shrink-0">
                  <Info size={16}/>
                </div>
                <p className="text-primary/80 leading-relaxed">Bu rol için tanımladığınız yetkiler, bu role atanan tüm personeller için anında geçerli olacaktır.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Permissions */}
        <div className="w-full lg:w-2/3 p-6 bg-bgAlt">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2 text-text">
                <Shield className="w-5 h-5 text-primary" />
                Yetkiler
              </h3>
              <p className="text-xs text-secondary mt-1">Bu role hangi modüllerin ve işlemlerin açık olacağını seçin.</p>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-borderColor p-2 shadow-sm">
            <DynamicPermissionTree 
              permissionsSchema={permissionsSchema}
              activeModules={activeModules} 
              permissions={permissions} 
              onChange={setPermissions} 
              readOnly={isReadOnly}
            />
          </div>
        </div>

      </div>
    </Dialog>
  );
};